import type { IntlShape } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { isCodedError } from '~/common/errors/CodedError';
import { intlService } from '~/i18n/IntlService';
import Configuration from '~/services/configuration';
import { Uuid } from '~/types/Uuid';

import type { Message as MessageType } from '../types/chat';
import {
  CITATIONS_FAILURE_KEY,
  HIGHLIGHT_GENERATION_FAILED_KEY,
  SOME_HIGHLIGHTS_FAILED_KEY,
} from './constants';
import { SseRequestProps } from './hooks/useSse';

export const replyAiMessage = (): MessageType => ({
  id: uuid(),
  text: '',
  timestamp: new Date(),
  userType: 'other',
});

export const replyUserMessage = (text: string): MessageType => ({
  id: uuid(),
  text,
  timestamp: new Date(),
  userType: 'current',
});

export const getStreamEndpoint = (id: Uuid) =>
  `${Configuration.sylvanusEndpoint}/api/v1/conversation/${id}/ai_message/stream`;

export const SSE_REQUEST_PROPS: SseRequestProps = {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  openWhenHidden: true,
};

export const parseStreamEndMessage = (message: string) => {
  const [error, details] = message.split(':');
  return (details ?? error).trim();
};

export const getStaticContextActions = (
  intl: IntlShape,
  isWorkflowOptionEnabled = false,
  isWorkflowRoutingEnabled = false,
) => {
  const comingSoonText = intl.formatMessage({
    id: 'ChatBotUtils.comingSoon',
    defaultMessage: 'Coming soon',
    description: 'Chip indicating feature is not yet available',
  });

  return [
    {
      label: intl.formatMessage({
        id: 'ChatBotUtils.searchRange',
        defaultMessage: 'Search Range',
        description: 'Label for search range selection',
      }),
      options: [
        {
          label: intl.formatMessage({
            id: 'ChatBotUtils.documents',
            defaultMessage: 'Documents',
            description: 'Option to search within documents',
          }),
          value: 'documents',
        },
        {
          label: intl.formatMessage({
            id: 'ChatBotUtils.workflow',
            defaultMessage: 'Workflow',
            description: 'Option to search within workflow',
          }),
          value: 'workflow',
          disabled: !isWorkflowOptionEnabled,
          isActive: isWorkflowOptionEnabled,
          leftIcon: isWorkflowOptionEnabled ? ('filter' as const) : undefined,
          chips:
            isWorkflowOptionEnabled || isWorkflowRoutingEnabled
              ? undefined
              : [
                  {
                    text: comingSoonText,
                  },
                ],
        },
        {
          label: intl.formatMessage({
            id: 'ChatBotUtils.clauseLibrary',
            defaultMessage: 'Clause Library',
            description: 'Option to search within clause library (coming soon)',
          }),
          value: 'clause-library',
          disabled: true,
          chips: [
            {
              text: comingSoonText,
            },
          ],
        },
        {
          label: intl.formatMessage({
            id: 'ChatBotUtils.knowledgeCenter',
            defaultMessage: 'Knowledge Center',
            description:
              'Option to search within knowledge center (coming soon)',
          }),
          value: 'kownledge-center',
          disabled: true,
          chips: [
            {
              text: comingSoonText,
            },
          ],
        },
      ],
    },
  ];
};

export const is424Error = (error: unknown) => {
  return isCodedError(error) && error.cause?.statusCode === 424;
};

export const getCitationWarningMessage = (key: string) => {
  const intl = intlService.getIntl();
  switch (key) {
    case CITATIONS_FAILURE_KEY:
      return intl.formatMessage({
        id: 'ChatBotUtils.citationsFailure',
        defaultMessage: 'Citations could not be generated.',
        description: 'Error message when citations generation fails',
      });
    case SOME_HIGHLIGHTS_FAILED_KEY:
      return intl.formatMessage({
        id: 'ChatBotUtils.someHighlightsFailed',
        defaultMessage:
          'Some highlights could not be generated for the citations.',
        description:
          'Warning message when some citation highlights fail to generate',
      });
    case HIGHLIGHT_GENERATION_FAILED_KEY:
      return intl.formatMessage({
        id: 'ChatBotUtils.highlightGenerationFailed',
        defaultMessage: 'There was an issue generating citation highlights.',
        description: 'Error message when citation highlight generation fails',
      });
    default:
      return intl.formatMessage({
        id: 'ChatBotUtils.citationsGeneralError',
        defaultMessage: 'There was an issue generating citations.',
        description: 'General error message for citation generation issues',
      });
  }
};
